import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { garmentService } from '../../Services';

import GarmentCard from '../../Components/GarmentCard';
import SkeletonCard from '../../Components/SkeletonCard';

import { Container } from '../../Components/ui/Container/Container';
import Section from '../../Components/ui/Section/Section';
import { Typography } from '../../Components/ui/Typography/Typography';
import { useTrackScreenView } from '../../Hooks/use-track-screen-view';
import styles from './index.module.scss';

export const Collections = () => {
    const { category } = useParams();
    useTrackScreenView('screen_collection_view');

    return (
        <Section>
            <Container>
                <Section top="none" bottom="lg">
                    <Typography variant="heading1" component="h1">
                        {category
                            ? category.charAt(0).toUpperCase() + category.slice(1) + 's'
                            : null}
                    </Typography>
                </Section>
                <GarmentsList />
            </Container>
        </Section>
    );
};

const GarmentsList = () => {
    const { category } = useParams();

    const fetchGarments = () => {
        if (category) {
            return garmentService.fetchGarmentsByCategory(category);
        } else {
            return garmentService.fetchAllGarments();
        }
    };

    const { isPending, isError, data, error } = useQuery({
        queryKey: ['garments', category],
        queryFn: fetchGarments,
    });

    if (isPending) {
        // Show skeleton cards while loading
        return (
            <div className={styles.home}>
                {Array.from({ length: 9 }).map((_, index) => (
                    <SkeletonCard key={index} />
                ))}
            </div>
        );
    }

    if (isError) {
        return (
            <div className="container">
                <span>Error: {error.message}</span>
            </div>
        );
    }

    if (!data || !data.garments || data.garments.length === 0) {
        return (
            <div className="container">
                <Typography>No garments found in this category.</Typography>
            </div>
        );
    }

    return (
        <div className={styles.home}>
            {data?.garments.map((garment, index) => {
                const images = garment.variants[0]?.images ?? [];
                const flatIndex = images.findIndex((image) => image.tag === 'flat');
                const firstImageIndex = flatIndex >= 0 ? flatIndex : 0;
                const firstImage = images[firstImageIndex];
                const secondImage =
                    images.find((image) => image.tag === 'model') ??
                    images.find((_, i) => i !== firstImageIndex);

                return (
                    <GarmentCard
                        key={garment.id}
                        id={garment.id}
                        title={garment.title}
                        firstImageUrl={firstImage?.url || ''}
                        secondImageUrl={secondImage?.url || ''}
                        price={garment.variants[0]?.price?.amount ?? ''}
                        partnerId={garment.partner_id}
                        style={{ animationDelay: `${index * 150}ms` }} // Staggered delay
                    />
                );
            })}
        </div>
    );
};
